import { bestSongToPlan, type PlanSongCandidate } from 'fym-shared/src/music24/bestSongToPlan';
import date from 'date-and-time';
import type { IntlShape } from 'react-intl';
import { STUDIO_THEMES, type StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import type { AssetSpecId } from 'fym-shared/src/music24/assetSpecs';
import type {
  ArchivedPostFile,
  ArchivedPostFiles,
  PostsBoard,
  PostsBoardFile,
  PostsBoardPost,
  PostsBoardPostedRow,
  PostsBoardScheduledRow,
  PostsBoardSong,
} from '../../../../../../music24-api/src/kit/posts/postsBoardTypes';
import { SerializedKitAsset } from '../../../../redux/api/fymAPI.types';
import { kitVariantLabel } from '../kitLabels';
import { studioThemeMessages } from '../studio/studioThemeMessages';
import { SerializedReel } from '../types';
import { AssetViewerItem, kitAssetViewerItem, reelViewerItem } from '../viewer/viewerItems';
import { postsMessages } from './postsMessages';
import { kitAssetThumbPicture, type KitPicture } from '../kitPictureSrc';

/**
 * "Rollout day −7", "Rollout day 0", "Rollout day +7" of a day offset (owner request 2026-10-09:
 * a Posts row says which rollout step a file or a post is for). Null for no offset.
 */
export function rolloutDayText(intl: IntlShape, offset: number | null | undefined): string | null {
  if (offset === null || offset === undefined) {
    return null;
  }
  let label = '0';
  if (offset < 0) {
    label = `−${Math.abs(offset)}`;
  } else if (offset > 0) {
    label = `+${offset}`;
  }
  return intl.formatMessage(postsMessages.rolloutDay, { offset: label });
}

/** The parts of a row detail, joined with " · " (the empty parts left out). */
export function joinDetail(parts: ReadonlyArray<string | null>): string {
  return parts.filter((part): part is string => part !== null && part !== '').join(' · ');
}

export type {
  ArchivedPostFile,
  ArchivedPostFiles,
  PostsBoard,
  PostsBoardFile,
  PostsBoardPost,
  PostsBoardPostedRow,
  PostsBoardScheduledRow,
  PostsBoardSong,
};

/**
 * The pure rules of the Posts page (design assets-rethink c1, c2, c3). The API gives the lanes
 * (`assets.posts`); this file groups them for the screen and names the files. Tested without React.
 */

/** The channel filter values (`?platform=`), in the order of the chips (design c1). */
export const POSTS_PLATFORMS = ['instagram', 'tiktok', 'youtube', 'facebook', 'threads', 'x'] as const;
export type PostsPlatform = (typeof POSTS_PLATFORMS)[number];

export function parsePostsPlatform(value: string | null | undefined): PostsPlatform | undefined {
  return POSTS_PLATFORMS.find((one) => one === value);
}

/** The rows a lane shows before "Show N more" (design c1). */
export const POSTS_LANE_PAGE = 5;

export type PostsLaneId = 'ready' | 'scheduled' | 'posted';

/**
 * The server and the web app keep two copies of one wire format (music24-api `serializeKitAsset.ts`
 * and `fymAPI.types.ts`). The tRPC answer carries the server copy; the viewer reads the web copy.
 */
export function webAssetOf(file: PostsBoardFile): SerializedKitAsset | null {
  return file.asset === null ? null : (file.asset as unknown as SerializedKitAsset);
}

export function webReelOf(file: PostsBoardFile): SerializedReel | null {
  return file.reel === null ? null : (file.reel as unknown as SerializedReel);
}

/** The label of a file: "Story (Out now)", "Countdown (7 days to go)", "AI reel 30s". */
export function postFileLabel(intl: IntlShape, file: PostsBoardFile): string {
  if (file.source === 'reel') {
    return intl.formatMessage(postsMessages.reelLabel, { length: file.variant });
  }
  const asset = webAssetOf(file);
  const themeId = file.themeId as StudioThemeId;
  const messages = studioThemeMessages[themeId];
  const name = messages === undefined ? file.themeId : intl.formatMessage(messages.name);
  const theme = STUDIO_THEMES[themeId];
  const namesVariant =
    themeId === 'story' || themeId === 'feedPost' || themeId === 'countdown' || (theme?.outputs.length ?? 0) > 1;
  if (!namesVariant || asset === null) {
    return name;
  }
  return `${name} (${kitVariantLabel(intl, { variant: file.variant, specId: asset.specId as AssetSpecId })})`;
}

/**
 * The small picture of a file (the thumbnail, else the poster, else an image) with its imgproxy
 * source (QA 2026-10-28, mob-7): `kitPictureSrc` draws it
 * at the row size, not the full file. A reel poster has no source yet: the file route.
 */
export function postFileThumbPicture(file: PostsBoardFile): KitPicture | null {
  const reel = webReelOf(file);
  if (reel !== null) {
    const poster = reel.video?.posterUrl ?? null;
    return poster === null ? null : { url: poster, sourceUrl: null };
  }
  const asset = webAssetOf(file);
  if (asset === null || asset.status !== 'ready' || asset.fileRemoved === true) {
    return null;
  }
  return kitAssetThumbPicture(asset, asset.durationSec !== null);
}

/** The viewer item of a ready file; null while it is made or failed. */
export function postFileViewerItem(file: PostsBoardFile): AssetViewerItem | null {
  if (file.state !== 'ready') {
    return null;
  }
  const reel = webReelOf(file);
  if (reel !== null) {
    return reelViewerItem(reel);
  }
  const asset = webAssetOf(file);
  return asset === null ? null : kitAssetViewerItem(asset);
}

/** Every ready file of the board, once each, in lane order. */
export function boardFiles(board: Pick<PostsBoard, 'ready' | 'scheduled' | 'posted'>): PostsBoardFile[] {
  const seen = new Set<string>();
  const files: PostsBoardFile[] = [];
  const add = (file: PostsBoardFile): void => {
    if (!seen.has(file.key)) {
      seen.add(file.key);
      files.push(file);
    }
  };
  board.ready.forEach(add);
  board.scheduled.forEach((row) => add(row.file));
  board.posted.forEach((row) => add(row.file));
  return files;
}

/** The viewer items of one song: the files the viewer steps through. */
export function viewerItemsOfSong(files: ReadonlyArray<PostsBoardFile>, catalogItemUuid: string): AssetViewerItem[] {
  return files
    .filter((file) => file.catalogItemUuid === catalogItemUuid)
    .map(postFileViewerItem)
    .filter((item): item is AssetViewerItem => item !== null);
}

/** The file whose shown revision or any viewer key is `assetUuid` (the `?asset=` of the address). */
export function fileOfAssetUuid(files: ReadonlyArray<PostsBoardFile>, assetUuid: string): PostsBoardFile | null {
  return files.find((file) => file.uuid === assetUuid) ?? null;
}

const DAY_MS = 86_400_000;

function dayKey(at: Date): string {
  return date.format(at, 'YYYY-MM-DD');
}

/** Whole days from today to a `YYYY-MM-DD` release day, in the local zone. */
export function daysUntil(releaseDate: string, now: Date): number {
  const release = date.parse(releaseDate.slice(0, 10), 'YYYY-MM-DD');
  const today = date.parse(dayKey(now), 'YYYY-MM-DD');
  return Math.round((release.getTime() - today.getTime()) / DAY_MS);
}

export interface ReadySongGroup {
  catalogItemUuid: string | null;
  files: PostsBoardFile[];
}

/** The Ready lane by song, in the order of the newest file of each song (design c1). */
export function groupReadyBySong(files: ReadonlyArray<PostsBoardFile>): ReadySongGroup[] {
  const groups: ReadySongGroup[] = [];
  for (const file of files) {
    const group = groups.find((one) => one.catalogItemUuid === file.catalogItemUuid);
    if (group === undefined) {
      groups.push({ catalogItemUuid: file.catalogItemUuid, files: [file] });
    } else {
      group.files.push(file);
    }
  }
  return groups;
}

/** "Forests · out in 238 days", "Swamps · out 7 Sep", "Forests · out today". */
export function songHeading(intl: IntlShape, song: PostsBoardSong | undefined, now: Date): string {
  if (song === undefined) {
    return intl.formatMessage(postsMessages.noSong);
  }
  if (song.releaseDate === null) {
    return song.title;
  }
  const days = daysUntil(song.releaseDate, now);
  if (days > 0) {
    return intl.formatMessage(postsMessages.songOutIn, { song: song.title, days });
  }
  if (days === 0) {
    return intl.formatMessage(postsMessages.songOutToday, { song: song.title });
  }
  return intl.formatMessage(postsMessages.songOut, {
    song: song.title,
    date: intl.formatDate(date.parse(song.releaseDate.slice(0, 10), 'YYYY-MM-DD'), { day: 'numeric', month: 'short' }),
  });
}

/** "Made today, 13:40" or "Made 3 Oct". */
export function madeText(intl: IntlShape, createdAt: string, now: Date): string {
  const at = new Date(createdAt);
  if (dayKey(at) === dayKey(now)) {
    return intl.formatMessage(postsMessages.madeToday, { time: intl.formatTime(at) });
  }
  return intl.formatMessage(postsMessages.madeOn, { date: intl.formatDate(at, { day: 'numeric', month: 'short' }) });
}

export interface ScheduledDayGroup {
  key: string;
  rows: PostsBoardScheduledRow[];
}

/** The Scheduled lane by local day (the API sorts by time). */
export function groupScheduledByDay(rows: ReadonlyArray<PostsBoardScheduledRow>): ScheduledDayGroup[] {
  const groups: ScheduledDayGroup[] = [];
  for (const row of rows) {
    const key = dayKey(new Date(row.post.scheduledAt));
    const last = groups[groups.length - 1];
    if (last !== undefined && last.key === key) {
      last.rows.push(row);
    } else {
      groups.push({ key, rows: [row] });
    }
  }
  return groups;
}

/** "Today · Fri 9 Oct", "Tomorrow · Sat 10 Oct", "Mon 12 Oct". */
export function scheduledDayHeading(intl: IntlShape, key: string, now: Date): string {
  const day = date.parse(key, 'YYYY-MM-DD');
  const label = intl.formatDate(day, { weekday: 'short', day: 'numeric', month: 'short' });
  const diff = Math.round((day.getTime() - date.parse(dayKey(now), 'YYYY-MM-DD').getTime()) / DAY_MS);
  if (diff === 0) {
    return intl.formatMessage(postsMessages.dayToday, { date: label });
  }
  if (diff === 1) {
    return intl.formatMessage(postsMessages.dayTomorrow, { date: label });
  }
  return label;
}

export interface PostedPeriodGroup {
  /** `week` for the last 7 days, else `YYYY-MM`. */
  key: string;
  rows: PostsBoardPostedRow[];
}

/** The Posted lane by period: "This week", then one group per month (design c1). */
export function groupPostedByPeriod(rows: ReadonlyArray<PostsBoardPostedRow>, now: Date): PostedPeriodGroup[] {
  const groups: PostedPeriodGroup[] = [];
  const weekStart = now.getTime() - 7 * DAY_MS;
  for (const row of rows) {
    const at = new Date(row.lastAt);
    const key = at.getTime() >= weekStart ? 'week' : date.format(at, 'YYYY-MM');
    const last = groups[groups.length - 1];
    if (last !== undefined && last.key === key) {
      last.rows.push(row);
    } else {
      groups.push({ key, rows: [row] });
    }
  }
  return groups;
}

export function postedPeriodHeading(intl: IntlShape, key: string): string {
  if (key === 'week') {
    return intl.formatMessage(postsMessages.periodThisWeek);
  }
  return intl.formatDate(date.parse(key, 'YYYY-MM'), { month: 'long', year: 'numeric' });
}

/** The newest failed post of a Posted row when no post of it went out; else null. */
export function failedPostOf(row: PostsBoardPostedRow): PostsBoardPost | null {
  if (row.posts.some((post) => post.status === 'posted')) {
    return null;
  }
  return row.posts.find((post) => post.status === 'failed') ?? null;
}

/** The first post of a row that the daily read linked ("Open in Social"). */
export function socialPostIdOf(row: PostsBoardPostedRow): number | null {
  return row.posts.find((post) => post.socialPostId !== null)?.socialPostId ?? null;
}

/** The channels of a list of posts, once each, in their order. */
export function platformsOf(posts: ReadonlyArray<PostsBoardPost>): string[] {
  return [...new Set(posts.map((post) => post.platform))];
}

/** "12.4K views" or "894 likes" (views first); null with no number. */
export function numbersText(intl: IntlShape, views: number | null, likes: number | null): string | null {
  const compact = (value: number): string =>
    intl.formatNumber(value, { notation: 'compact', maximumFractionDigits: 1 });
  if (views !== null) {
    return intl.formatMessage(postsMessages.views, { count: compact(views) });
  }
  if (likes !== null) {
    return intl.formatMessage(postsMessages.likes, { count: compact(likes) });
  }
  return null;
}

/** The files that "Select" can pick: every file of the Ready and Posted lanes. */
export function selectableFiles(board: Pick<PostsBoard, 'ready' | 'posted'>): PostsBoardFile[] {
  return [...board.ready, ...board.posted.map((row) => row.file)];
}

/** True while a file is queued or made (the poll runs only then, and only with the socket down). */
export function isBoardMaking(board: Pick<PostsBoard, 'making'> | undefined): boolean {
  return board !== undefined && board.making.some((file) => file.state === 'queued' || file.state === 'working');
}

/** True when the board has no file at all (design d2 empty state). */
export function isBoardEmpty(board: Pick<PostsBoard, 'making' | 'ready' | 'scheduled' | 'posted'>): boolean {
  return (
    board.making.length === 0 && board.ready.length === 0 && board.scheduled.length === 0 && board.posted.length === 0
  );
}

/** The text of the outcome column of the archive (design c2). */
export function archiveOutcomeText(intl: IntlShape, row: ArchivedPostFile): string {
  if (row.outcome === 'never' || row.firstPostedAt === null) {
    return intl.formatMessage(postsMessages.outcomeNever);
  }
  return intl.formatMessage(postsMessages.outcomePosted, {
    date: intl.formatDate(new Date(row.firstPostedAt), { day: 'numeric', month: 'short' }),
  });
}

/**
 * Where "Plan a rollout" of the empty board goes (owner decision 2026-10-12): the Plan tab of the
 * song that the board shows (the Posts tab of a release, or the Posts page with a song filter),
 * else the Plan tab of the best song of the artist (`bestSongToPlan`: the next release, else the
 * newest one), else "Add my song" when the artist has no song.
 */
export function emptyBoardPlanTarget(
  catalogItemUuid: string | undefined,
  songs: ReadonlyArray<PlanSongCandidate>,
  today: string
): { kind: 'plan'; catalogItemUuid: string } | { kind: 'addSong' } {
  if (catalogItemUuid !== undefined && catalogItemUuid !== '') {
    return { kind: 'plan', catalogItemUuid };
  }
  const best = bestSongToPlan(songs, today);
  if (best !== null) {
    return { kind: 'plan', catalogItemUuid: best };
  }
  return { kind: 'addSong' };
}
